import { useQuery } from '@tanstack/react-query';
import { api } from './api/client';
import logoMark from './assets/logo-mark.png';
import './styles/app.css';

/**
 * Cycle 0 walking skeleton.
 *
 * This screen exists to prove the whole stack is connected: the SPA calls the API, the
 * API reaches PostgreSQL and Redis, and the response validates against the contract in
 * packages/shared. Cycle 1 replaces it with the real sign-in.
 */
export function App() {
  const {
    data: health,
    isPending,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    // Re-polls on its own every 10s, so a retry would only delay showing the user that
    // something is wrong. Surface the failure immediately and let the interval recover.
    refetchInterval: 10_000,
    retry: false,
  });

  return (
    <main className="shell">
      <header className="brand">
        <img src={logoMark} alt="" width={56} height={40} />
        <div>
          <h1 className="wordmark">
            <span className="wm-blue">Handy</span> <span className="wm-orange">Nest</span>
          </h1>
          <p className="tagline">Verified local service professionals</p>
        </div>
      </header>

      <section className="card" aria-labelledby="status-heading">
        <div className="card-h">
          <h2 id="status-heading">System status</h2>
          <span className="spacer" />
          <button className="btn" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? 'Checking…' : 'Check again'}
          </button>
        </div>

        <div className="card-b">
          {isPending && <p className="muted">Contacting the API…</p>}

          {error && (
            <div className="note crit" role="alert">
              <strong>Cannot reach the API.</strong>{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
              <p className="muted small">
                Is the stack running? Try <code>pnpm dev</code> from the repository root.
              </p>
            </div>
          )}

          {health && (
            <>
              <div className={`overall ${health.status}`} data-testid="overall-status">
                <span className="dot" aria-hidden="true" />
                <strong>{health.status === 'ok' ? 'All systems operational' : 'Degraded'}</strong>
                <span className="muted">
                  {health.service} v{health.version} · up {health.uptimeSeconds}s
                </span>
              </div>

              <ul className="deps">
                {health.dependencies.map((dep) => (
                  <li key={dep.name} className="dep" data-testid={`dep-${dep.name}`}>
                    <span className={`pill ${dep.status === 'up' ? 'good' : 'crit'}`}>
                      {dep.status}
                    </span>
                    <span className="dep-name">{dep.name}</span>
                    <span className="dep-latency">{dep.latencyMs.toFixed(1)} ms</span>
                    {dep.detail && <span className="dep-detail">{dep.detail}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <footer className="foot">
        <span>Cycle 0 — foundation</span>
        <span>Operations Process Flow v3.0</span>
      </footer>
    </main>
  );
}
